import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { getBrandMagnet } from "apis/brand";
import { get, patch } from "utils/request";

import { BrandContext } from "components/context/BrandContext";
import Loading from "components/atoms/Loading";
import MagnetDetailsForm from "components/organisms/magnets/MagnetDetailsForm";
import Button from "components/atoms/buttons/Button";

import {
  handleSetErrorAndSwitchFormState,
  uploadImageToS3,
  validateInputImageWithExistingImageUrl,
} from "utils/HelperFunctions";
import MagnetCreateForm from "components/organisms/magnets/MagnetCreateForm";
import FormTab from "components/atoms/forms/FormTab";
import { useRouter } from "components/hooks";
import { isMagnetInPublishedState } from "utils/helpers";
import { getMagnetDetailsEndpoint, MAX_MAGNET_BENEFIT_LENGTH, TOAST_CONFIG } from "../../../constants";

type FormFieldsType = "name" | "image_url" | "max_supply" | "description" | "magnetized_because";

const formFields = {
  magnetized_because: "1",
  name: "0",
  image_url: "0",
  max_supply: "0",
  description: "0",
  benefits: "1",
};

export default function EditMagnetPage() {
  const [loading, setLoading] = useState(false);
  // const [searchParams, setSearchParams] = useSearchParams();
  // const currentStep: number = Number(searchParams.get("step"));

  const { magnetId } = useParams();
  const { brandId, loading: brandInfoLoading } = useContext(BrandContext);
  const [magnetDetails, setMagnetDetails] = useState<BrandMagnetDetails | null>(null);

  const getAndSetMagnetInfo = (bId: string, mId: string) => {
    get(getMagnetDetailsEndpoint(bId, mId), {
      processData: ({ data }) => {
        setMagnetDetails(data);
      },
      processError: () => {
        toast.error("Failed to get error Magnet details.", TOAST_CONFIG);
      },
    });
  };

  useEffect(() => {
    if (brandId && magnetId) {
      getAndSetMagnetInfo(brandId, magnetId);
    } else {
      toast.error("Missing brandId or magnetId", TOAST_CONFIG);
    }
  }, [magnetId, brandId]);

  if (!magnetDetails || brandInfoLoading) {
    return <Loading />;
  }

  return magnetDetails && brandId && magnetId ? (
    <>
      {loading && <Loading />}
      {/*  eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <EditMagnet magnetDetails={magnetDetails} setLoading={setLoading} magnetId={magnetId} brandId={brandId} />
    </>
  ) : null;
}

const schema = yup.object().shape({
  name: yup.string().trim().required("* Required field"),
  description: yup.string().trim().required("* Required field"),
  image: validateInputImageWithExistingImageUrl("image_url"),
  magnetized_because: yup.string().trim().required("* Required field"),
  benefits: yup.array().of(
    yup.object().shape({
      // if image_url is not present than we move toward validating image
      // when we create a new feature, image_url is not present for that feature
      // So we validate if image_url is not present, then we apply image validation.
      image: validateInputImageWithExistingImageUrl("image_url"),
      title: yup.string().required("* Required field"),
      description: yup
        .string()
        .required("* Required field")
        .test(
          "len check",
          `Max Length is ${MAX_MAGNET_BENEFIT_LENGTH}`,
          (val) => (val || "")?.length < MAX_MAGNET_BENEFIT_LENGTH + 1,
        ),
    }),
  ),
});

type IProps = {
  magnetDetails: BrandMagnetDetails;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  brandId: string;
  magnetId: string;
};

function EditMagnet({ magnetDetails, setLoading, brandId, magnetId }: IProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const formMethods = useForm<CreateUpdateMagnetFormType>({
    resolver: yupResolver(schema),
    // @ts-ignore
    defaultValues: magnetDetails,
    mode: "onChange",
  });

  const { handleSubmit, setError, trigger } = formMethods;

  const router = useRouter();

  const handleGoBack = () => router.go(-1);

  async function uploadFeatureImagesToS3AndReturnMappedImages(
    features: CreateUpdateMagnetFormBenefit[],
  ): Promise<MagnetBenefit[]> {
    const featuresWithImageUrls: MagnetBenefit[] = [];
    const uploadFeatureImagesResponse = await Promise.allSettled(features.map((f) => uploadImageToS3(f.image[0])));
    // TODO: Error handing for rejected images.
    uploadFeatureImagesResponse.forEach((r, idx) => {
      // @ts-ignore
      const feature: CreateUpdateMagnetFormBenefit = { ...features[idx] };
      delete feature.image;
      if (r.status === "fulfilled" && r.value) {
        // @ts-ignore
        feature.image_url = r.value as string;
      } else {
        // TODO: Set Errors Here.
      }
      // @ts-ignore
      featuresWithImageUrls.push(feature);
    });
    return featuresWithImageUrls;
  }

  const handleGetUpdatedFeatures = async (benefits: CreateUpdateMagnetFormBenefit[]) => {
    const updatedPayload: CreateUpdateMagnetFormBenefit[] = [];

    const benefitsWithUpdatedImages: CreateUpdateMagnetFormBenefit[] = [];

    benefits.forEach((f) => {
      if (f.image?.length) {
        benefitsWithUpdatedImages.push(f);
      } else {
        updatedPayload.push(f);
      }
    });

    // Upload Image
    const benefitsWitUploadedImages = await uploadFeatureImagesToS3AndReturnMappedImages(benefitsWithUpdatedImages);

    return [...updatedPayload, ...benefitsWitUploadedImages];
  };

  const handleSetFormStep = (step: string) => {
    setCurrentStep(Number(step));
  };

  // function for posting data to the server
  const handleMagnetDetailsUpdate = async (data: CreateUpdateMagnetFormType) => {
    if (!brandId || !magnetId) {
      toast.error("Missing Brand Id or Magnet Id.");
      return;
    }
    setLoading(true);

    const handleMagnetUpdate = () => {
      toast.success("Magnet Edited Successfully!", TOAST_CONFIG);
      setLoading(false);
      handleGoBack();
    };

    const handleMagnetUpdateError = (errorMessage: string, error: any, errorData: any) => {
      toast.error(errorMessage, TOAST_CONFIG);
      const step = handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
      if (step) handleSetFormStep(step);
      setLoading(false);
    };

    const updatedFeatures = await handleGetUpdatedFeatures(data.benefits);
    const magnetImage = data.image?.length ? await uploadImageToS3(data.image[0]) : undefined;

    const payload = {
      // Can be only updated till the magnet is published
      ...(!isMagnetInPublishedState(magnetDetails.status) && {
        name: data.name,
        description: data.description,
        max_supply: data.max_supply,
        image_url: magnetImage,
      }),
      // Can be updated any time.
      magnetized_because: data.magnetized_because,
      benefits: JSON.stringify(updatedFeatures),
    };

    if (data.members_csv) {
      // @ts-ignore
      payload.members_csv = data.members_csv;
    }

    patch(getBrandMagnet(brandId, magnetId), payload, {
      processData: handleMagnetUpdate,
      processError: handleMagnetUpdateError,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  const handleFormErrors = (errorsObj: any) => {
    let step: string = "";
    Object.keys(errorsObj).some((errorKey: string) => {
      const errorKeyWithType = errorKey as FormFieldsType;
      if (Object.keys(formFields).includes(errorKeyWithType)) {
        step = formFields[errorKeyWithType];
      }
      if (Number(step) === currentStep) return true;
      return false;
    });
    if (step) {
      handleSetFormStep(step);
    }
  };

  const getForm = (step: Number) => {
    switch (step) {
      case 0:
        return <MagnetCreateForm isMagnetPublished={isMagnetInPublishedState(magnetDetails.status)} />;
      case 1:
        return <MagnetDetailsForm />;
      default:
        return <MagnetCreateForm isMagnetPublished={isMagnetInPublishedState(magnetDetails.status)} />;
    }
  };

  const handleValidationAndSetFormStep = async (step: string) => {
    if (await trigger()) {
      handleSetFormStep(step);
    }
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...formMethods}>
      <div className="mt-4 font-sora">
        <div className="main-container flex min-h-[82vh] flex-col">
          <div>
            <div className="main-container">
              <div className="mb-5 flex items-center font-sora font-semibold text-white xl:text-4xl 2xl:text-5xl">
                <h1>Edit Magnet</h1>
              </div>
              {/* Form Tabs */}
              <div className="flex gap-5">
                <FormTab
                  label="Magnet Edit"
                  isActive={currentStep === 0}
                  handleOnClick={() => handleValidationAndSetFormStep("0")}
                />
                <FormTab
                  label="Magnet Details Edit"
                  isActive={currentStep === 1}
                  handleOnClick={() => handleValidationAndSetFormStep("1")}
                />
              </div>
              {/* Forms */}
              <div className="mt-4">{getForm(currentStep)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full justify-end space-x-2 px-3 py-5 shadow-2xl">
        <Button
          text="UPDATE MAGNET INFO"
          additionalClasses="xl:px-6 xl:py-3 xl:text-xs 2xl:text-sm 2xl:px-8 2xl:py-4"
          onClick={handleSubmit(handleMagnetDetailsUpdate, handleFormErrors)}
          variant="rustPinkSolid"
        />
      </div>
    </FormProvider>
  );
}
